import matter from 'gray-matter';
import { remark } from 'remark';
import remarkGfm from 'remark-gfm';
import remarkHtml from 'remark-html';
import { NewsPost, Example } from '../types/content';

// Initialize markdown processor
const processor = remark().use(remarkGfm).use(remarkHtml);

/**
 * Process markdown content to HTML
 */
export async function processMarkdown(content: string): Promise<string> {
  const result = await processor.process(content);
  return result.toString();
}

/**
 * Parse frontmatter and content from markdown file
 */
export function parseContent(fileContent: string) {
  const { data, content } = matter(fileContent);
  return { frontmatter: data, content };
}

/**
 * Generate slug from title
 */
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Get relative time (e.g., "2 hours ago")
 */
export function getRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  // Handle future dates
  if (diffInSeconds < 0) {
    const futureDiffInSeconds = Math.abs(diffInSeconds);
    if (futureDiffInSeconds < 86400) return 'today';
    if (futureDiffInSeconds < 604800) return `in ${Math.floor(futureDiffInSeconds / 86400)} days`;
    return formatDate(dateString);
  }

  // Handle past dates
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  
  return formatDate(dateString);
}

/**
 * Extract excerpt from content
 */
export function extractExcerpt(content: string, maxLength: number = 160): string {
  // Remove markdown formatting and HTML tags
  const plainText = content
    .replace(/#{1,6}\s/g, '') // Remove headers
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
    .replace(/\*(.*?)\*/g, '$1') // Remove italic
    .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove links, keep text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/\n+/g, ' ') // Replace newlines with spaces
    .trim();

  if (plainText.length <= maxLength) return plainText;
  
  // Find the last complete sentence within the limit
  const truncated = plainText.substring(0, maxLength);
  const lastSentence = truncated.lastIndexOf('.');
  
  if (lastSentence > maxLength * 0.7) {
    return truncated.substring(0, lastSentence + 1);
  }
  
  // If no good sentence break, truncate at word boundary
  const lastSpace = truncated.lastIndexOf(' ');
  return truncated.substring(0, lastSpace) + '...';
}

/**
 * Validate news post data
 */
export function validateNewsPost(data: any): data is NewsPost {
  return (
    typeof data.title === 'string' &&
    typeof data.dek === 'string' &&
    typeof data.source_name === 'string' &&
    typeof data.source_url === 'string' &&
    typeof data.published_at === 'string' &&
    Array.isArray(data.topics)
  );
}

/**
 * Validate example data
 */
export function validateExample(data: any): data is Example {
  const validGoals = ['summarize', 'plan', 'transform', 'reason', 'code-adjacent'];
  const validAudiences = ['kids', 'students', 'educators', 'workplace'];
  const validComplexities = ['intro', 'intermediate', 'advanced'];

  return (
    typeof data.title === 'string' &&
    validGoals.includes(data.goal) &&
    validAudiences.includes(data.audience) &&
    validComplexities.includes(data.complexity) &&
    typeof data.context === 'string' &&
    typeof data.prompt_template === 'string' &&
    Array.isArray(data.steps) &&
    typeof data.why_it_works === 'string'
  );
}

/**
 * Sort content by date (newest first)
 */
export function sortByDate<T extends { published_at: string }>(items: T[]): T[] {
  return items.sort((a, b) => 
    new Date(b.published_at).getTime() - new Date(a.published_at).getTime()
  );
}

/**
 * Filter content by topics
 */
export function filterByTopics<T extends { topics: string[] }>(
  items: T[], 
  topics: string[]
): T[] {
  if (topics.length === 0) return items;
  return items.filter(item => 
    topics.some(topic => item.topics.includes(topic))
  );
}

/**
 * Generate date-based URL for news post
 */
export function getNewsUrl(post: NewsPost): string {
  const date = new Date(post.published_at);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `/news/${year}/${month}/${post.slug}`;
}

/**
 * Extract date components from news post
 */
export function getDateComponents(post: NewsPost): { year: string; month: string } {
  const date = new Date(post.published_at);
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return { year, month };
}

/**
 * Find news post by date-based path or legacy slug
 */
export function findNewsPost(
  posts: NewsPost[], 
  slug: string, 
  year?: string, 
  month?: string
): NewsPost | undefined {
  // If year/month provided, use date-based lookup
  if (year && month) {
    return posts.find(post => {
      const { year: postYear, month: postMonth } = getDateComponents(post);
      return post.slug === slug && postYear === year && postMonth === month;
    });
  }
  
  // Fallback to slug-only lookup for legacy URLs
  return posts.find(post => post.slug === slug);
}